import { useState } from 'react';
import { Avatar, Box, Divider, IconButton, ListItemIcon, Menu, MenuItem, Typography } from '@mui/material';
import LogoutIcon from '@mui/icons-material/Logout';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { postLogout } from '@/api/endpoints';
import type { Me } from '@/api/types';

function initials(me: Me): string {
  const source = me.display_name?.trim() || me.email;
  const parts = source.split(/[\s@.]+/).filter(Boolean);
  return (parts[0]?.[0] ?? '') + (parts[1]?.[0] ?? '');
}

export function UserMenu({ me }: { me: Me }) {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);
  const queryClient = useQueryClient();

  const logout = useMutation({
    mutationFn: postLogout,
    onSuccess: () => {
      queryClient.clear();
      window.location.href = '/';
    },
  });

  return (
    <>
      <IconButton
        onClick={(e) => setAnchorEl(e.currentTarget)}
        size="small"
        aria-label="Account menu"
        aria-haspopup="menu"
        aria-expanded={open || undefined}
      >
        <Avatar sx={{ width: 32, height: 32, fontSize: 13, bgcolor: 'primary.main' }}>
          {initials(me).toUpperCase()}
        </Avatar>
      </IconButton>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Box sx={{ px: 2, py: 1, maxWidth: 280 }}>
          <Typography variant="subtitle2" noWrap>
            {me.display_name || me.email}
          </Typography>
          {me.display_name && (
            <Typography variant="caption" color="text.secondary" noWrap>
              {me.email}
            </Typography>
          )}
          {me.is_admin && (
            <Typography
              variant="caption"
              sx={{ display: 'block', color: 'secondary.main', fontWeight: 600, mt: 0.5 }}
            >
              Administrator
            </Typography>
          )}
        </Box>
        <Divider />
        <MenuItem onClick={() => logout.mutate()} disabled={logout.isPending}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          Sign out
        </MenuItem>
      </Menu>
    </>
  );
}
